/**
 * TextTooltip — WEB-ONLY implementation
 *
 * Why a separate .web.tsx file?
 * ─────────────────────────────
 * On web the tooltip must escape the FlatList/ScrollView stacking context so it
 * renders above sibling rows without transparency. Two React Native primitives fail:
 *
 *   • position:'absolute' inside FlatList — later rows paint over it regardless of
 *     zIndex because FlatList's scroll container creates a new CSS stacking context.
 *
 *   • Modal (react-native-web) — mounts a full-screen position:fixed overlay that
 *     blocks ALL scroll-wheel and pointer events on the page underneath it.
 *
 * Fix: ReactDOM.createPortal into document.body.
 * ───────────────────────────────────────────────
 * react-dom is already a project dependency (package.json). This file is ONLY
 * loaded by webpack (web build) — Metro never sees it, so mobile is unaffected.
 * The portal renders the bubble as a direct child of <body>, outside every
 * React Native stacking context. pointerEvents:'none' ensures the bubble never
 * intercepts scroll or click events on the page.
 */

import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Text, View} from 'react-native';
import {useScreenTheme} from '../../../hooks/useScreenTheme';
import {TextTooltipProps} from './TextTooltipProps';
import {createTextTooltipStyles} from './TextTooltip.styles';
import PieChartArrow from '@shared/assets/icons/PieChartArrow';
import PieChartArrowUp from '@shared/assets/icons/PieChartArrowUp';

// ── Module-level helpers (extracted to reduce TextTooltip cognitive complexity) ──

/**
 * Computes the top position (px) for the portal tooltip bubble.
 * Extracted from an IIFE inside the component to remove nesting-bonus CC.
 */
function computeBubbleTop(
  triggerRect: {top: number; bottom: number} | null,
  tooltipWidth: number,
  tooltipHeight: number,
  placement: 'above' | 'below',
): number {
  if (!triggerRect || tooltipWidth === 0) return -9999;
  if (placement === 'below') return triggerRect.bottom + 6;
  return tooltipHeight > 0 ? triggerRect.top - tooltipHeight - 6 : -9999;
}

/**
 * Computes the left position (px) for the portal tooltip bubble,
 * centered over the trigger and clamped to viewport bounds.
 * Extracted from an IIFE inside the component to remove nesting-bonus CC.
 */
function computeBubbleLeft(
  triggerRect: {left: number; width: number} | null,
  tooltipWidth: number,
  tooltipHorizontalAlign: string,
): number {
  if (!triggerRect || tooltipWidth === 0) return -9999;
  const viewportWidth = window.innerWidth;
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const centered = triggerCenter - tooltipWidth / 2;
  const clamped = Math.max(8, Math.min(centered, viewportWidth - tooltipWidth - 8));
  if (tooltipHorizontalAlign === 'right') {
    return Math.min(clamped, triggerRect.left);
  }
  return clamped;
}

/**
 * Computes the cursor CSS override for the tooltip wrapper.
 * Extracted to module level to remove the if/else-if chain from TextTooltip's CC.
 */
function computeCursorStyle(
  isTextMode: boolean,
  isWebOverflow: boolean,
  hasTooltip: boolean,
): any {
  if (isTextMode && !isWebOverflow) {return {cursor: 'default'};}
  if (!isTextMode && !hasTooltip) {return {cursor: 'default'};}
  return {};
}

/** Stable portal container — appended once to document.body and reused. */
let _portalRoot: HTMLElement | null = null;
function getPortalRoot(): HTMLElement {
  if (!_portalRoot) {
    _portalRoot = document.createElement('div');
    _portalRoot.id = 'tooltip-portal-root';
    // Never captures events; no stacking context of its own.
    _portalRoot.style.cssText =
      'position:absolute;top:0;left:0;width:0;height:0;overflow:visible;pointer-events:none;z-index:99999;';
    document.body.appendChild(_portalRoot);
  }
  return _portalRoot;
}

const TextTooltip: React.FC<TextTooltipProps> = ({
  // text mode
  text,
  fallbackText = '--',
  numberOfLines = 2,
  textStyle,
  accessibilityLabel,
  accessibilityRole,
  // children mode
  children,
  tooltipText,
  disabled = false,
  placement = 'above',
  minWidth,
  maxWidth,
  maxHeight = 300,
  tooltipHorizontalAlign = 'left',
  // shared
  onVisibilityChange,
  // web-only: when false the wrapper View does NOT receive tabIndex=0
  webFocusable = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipHeight, setTooltipHeight] = useState(0);
  /** Measured width of the tooltip bubble — needed to center and clamp it. */
  const [tooltipWidth, setTooltipWidth] = useState(0);
  /** True when the DOM <Text> element is visually truncated (ellipsis visible). */
  const [isWebOverflow, setIsWebOverflow] = useState(false);
  /**
   * Absolute-page-position bounding rect of the trigger, captured via
   * measureInWindow at hover time. Used to position the portal bubble.
   */
  const [triggerRect, setTriggerRect] = useState<{
    top: number;
    left: number;
    bottom: number;
    width: number;
    height: number;
  } | null>(null);

  const wrapperRef = useRef<View>(null);
  const textRef = useRef<any>(null);

  const tooltipTheme = useScreenTheme('tooltip') as any;
  const styles = createTextTooltipStyles(tooltipTheme);

  // Resolve tooltip text — skip empty/whitespace so '--' fallback never triggers a tooltip.
  const effectiveTooltipText = tooltipText ?? (text?.trim() ? text : undefined);
  const isTextMode = children === undefined;
  const hasTooltip = !disabled && !!effectiveTooltipText;

  // ── DOM overflow detection (drives cursor style) ─────────────────────────────
  useEffect(() => {
    if (!isTextMode || !textRef.current) return;
    const el = textRef.current as HTMLElement;
    const update = () => {
      setIsWebOverflow(
        el.scrollHeight > el.clientHeight + 4 ||
          el.scrollWidth > el.clientWidth,
      );
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, isTextMode, numberOfLines]);

  // ── show / hide ──────────────────────────────────────────────────────────────
  const show = () => {
    if (!hasTooltip) return;

    // Text mode: only show when text is actually truncated.
    if (isTextMode && textRef.current) {
      const el = textRef.current as HTMLElement;
      if (
        el.scrollHeight <= el.clientHeight + 4 &&
        el.scrollWidth <= el.clientWidth
      ) {
        return;
      }
    }

    if (!wrapperRef.current) return;

    // measureInWindow on React Native Web returns viewport-relative coordinates.
    // Add window scroll offsets to convert to absolute page coordinates, matching
    // the portal container which is positioned at (0,0) of <body>.
    wrapperRef.current.measureInWindow((x, y, w, h) => {
      const scrollTop =
        window.scrollY ?? document.documentElement.scrollTop ?? 0;
      const scrollLeft =
        window.scrollX ?? document.documentElement.scrollLeft ?? 0;
      setTriggerRect({
        top: y + scrollTop,
        left: x + scrollLeft,
        bottom: y + h + scrollTop,
        width: w,
        height: h,
      });
      setIsVisible(true);
      onVisibilityChange?.(true);
    });
  };

  const hide = () => {
    setIsVisible(false);
    setTooltipHeight(0);
    setTriggerRect(null);
    onVisibilityChange?.(false);
  };

  // ── Portal bubble position ────────────────────────────────────────────────────
  /**
   * Hide the bubble at -9999 until tooltipWidth is known so the centered
   * left position is computed in the same frame it first becomes visible
   * (no horizontal jump).
   */
  const bubbleTop = computeBubbleTop(triggerRect, tooltipWidth, tooltipHeight, placement);

  /**
   * Center the bubble over the trigger so the arrow points at the tapped
   * element, then clamp to viewport bounds (8 px margin).
   *
   * `'right'` alignment: additionally cap the left edge at the trigger's
   * own left x so the bubble never drifts right of the trigger when the
   * wrapperRef spans a wide flex container near the screen edge.
   */
  const bubbleLeft = computeBubbleLeft(triggerRect, tooltipWidth, tooltipHorizontalAlign);

  // ── Tooltip bubble ────────────────────────────────────────────────────────────
  const bubble =
    isVisible && hasTooltip && triggerRect ? (
      <View
        pointerEvents="none"
        onLayout={e => {
          const {height: h, width: w} = e.nativeEvent.layout;
          if (h > 0) setTooltipHeight(h);
          if (w > 0) setTooltipWidth(w);
        }}
        style={{
          position: 'absolute',
          top: bubbleTop,
          left: bubbleLeft,
          maxWidth: maxWidth ?? 400,
          minWidth: minWidth ?? 300,
          zIndex: 99999,
        }}>
        {placement === 'below' && (
          <View style={styles.arrowUp}>
            <PieChartArrowUp color={tooltipTheme.tooltipBackground} />
          </View>
        )}
        <View
          style={[
            styles.tooltipBox,
            {
              maxHeight,
              overflowY: 'auto',
              overflowX: 'hidden',
            } as any,
          ]}>
          <Text style={[styles.tooltipText, {textAlign: 'left'} as any]}>
            {effectiveTooltipText}
          </Text>
        </View>
        {placement === 'above' && (
          <View style={styles.arrowDown}>
            <PieChartArrow color={tooltipTheme.tooltipBackground} />
          </View>
        )}
      </View>
    ) : null;

  const cursorStyle = computeCursorStyle(isTextMode, isWebOverflow, hasTooltip);

  const hoverProps = hasTooltip
    ? ({onMouseEnter: show, onMouseLeave: hide} as any)
    : {};

  // On web, derive an accessible label: caller-supplied label, or the full (un-truncated) text.
  const webAccessibilityLabel =
    accessibilityLabel ?? (text?.trim() ? text : undefined);

  return (
    <View
      ref={wrapperRef}
      style={[styles.wrapper]}
      accessible={webFocusable}
      accessibilityRole={webFocusable ? accessibilityRole ?? 'text' : undefined}
      accessibilityLabel={webFocusable ? webAccessibilityLabel : undefined}
      // @ts-ignore — web-only ARIA/tabIndex props for keyboard focus
      tabIndex={webFocusable ? 0 : undefined}
      aria-label={webFocusable ? webAccessibilityLabel : undefined}
      {...hoverProps}>
      {/* Inner View is purely visual — hide it from the a11y tree
          so screen readers announce the wrapper label only once.
          Using View instead of TouchableOpacity avoids the implicit
          role="button" that TouchableOpacity adds on web, which caused
          screen readers to announce the element as "clickable". */}
      <View
        style={[cursorStyle]}
        accessible={false}
        importantForAccessibility="no-hide-descendants">
        {children ?? (
          <Text
            ref={textRef}
            style={[
              {color: tooltipTheme.text},
              textStyle, // Break long unbreakable tokens (e.g. numeric IDs without spaces)
              // so they wrap onto the next line instead of overflowing the cell.
              // Cast the whole object because React Native's TextStyle type does
              // not declare web-only CSS properties.
              {wordBreak: 'break-word', overflowWrap: 'break-word'} as any,
            ]}
            numberOfLines={numberOfLines}
            ellipsizeMode="tail"
            accessible={false}
            importantForAccessibility="no">
            {text || fallbackText}
          </Text>
        )}
      </View>

      {/* Render bubble outside the entire React Native tree via a DOM portal.
          This escapes every FlatList/ScrollView overflow/stacking context. */}
      {typeof document !== 'undefined' && bubble
        ? ReactDOM.createPortal(bubble, getPortalRoot())
        : null}
    </View>
  );
};

export default TextTooltip;
